/**
 * PackingSlip - Digital packing slip display
 *
 * Shows order details for packing verification:
 * - Order number (large, bold)
 * - Customer name
 * - Shipping address
 * - Item counts (POD/P&P breakdown)
 * - Bin label for multi-line orders
 * - Single-line indicator
 *
 * CRITICAL: Uses large, readable text for warehouse visibility.
 */
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, MapPin, Calendar, Box, Store, ExternalLink, LayoutPanelTop, PackageOpen } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { formatOrderNumber, type PortlessFulfilmentType } from '@threadheads/shared';

// BLU-197: work-bench labels are minted as BNCH-001, BNCH-002, … Used as a
// client-side fallback to tell a bench pill from a bin pill when the order
// payload doesn't carry an explicit isBench flag (getPackingOrder doesn't yet).
const BENCH_LABEL_PREFIX = 'BNCH-';

interface ShippingAddress {
  address1: string;
  address2?: string;
  city: string;
  province: string;
  zip: string;
  country: string;
}

interface PackingSlipProps {
  order: {
    orderId: string;
    orderName: string;
    customerName: string;
    customerEmail: string | null;
    shippingAddress: ShippingAddress;
    totalItems: number;
    podCount: number;
    papCount: number;
    binLabel: string | null;
    orderDate: string;
    isSingleLine: boolean;
    isPickup?: boolean;
    shopifyOrderId?: string | null;
    customerOrderIndex?: number | null;
    /** BLU-197: true when binLabel points at a work bench, not a bin. */
    isBench?: boolean;
    /**
     * Whether Portless assembles this parcel from two sources. Computed server-side
     * by the shared rule (@threadheads/shared) — the station never re-derives it.
     */
    fulfilmentType?: PortlessFulfilmentType;
  };
}

export function PackingSlip({ order }: PackingSlipProps) {
  const { t } = useTranslation('production');
  const { organization } = useOrganization();
  const formattedAddress = formatAddress(order.shippingAddress, t);
  const formattedDate = formatDate(order.orderDate);
  const shopifyOrderId = order.shopifyOrderId ? Number(order.shopifyOrderId) : null;
  const shopifyUrl = getShopifyAdminOrderUrl(organization?.shopify_domain, shopifyOrderId);

  return (
    <Card className="border-2">
      <CardContent className="p-6 space-y-4">
        {/* Order number - large and prominent, links to Shopify */}
        <div className="flex items-start justify-between">
          <div>
            {shopifyUrl ? (
              <a
                href={shopifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-1.5"
              >
                <h2 className="text-2xl font-bold group-hover:underline">{formatOrderNumber(order.orderName)}</h2>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ) : (
              <h2 className="text-2xl font-bold">{formatOrderNumber(order.orderName)}</h2>
            )}
            <div className="flex items-center gap-2">
              <p className="text-lg text-muted-foreground">{order.customerName}</p>
              {order.customerOrderIndex != null && order.customerOrderIndex >= 2 && (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs">
                  {t('packer.packingSlip.nthOrder', { orderOrdinal: getOrdinal(order.customerOrderIndex) })}
                </Badge>
              )}
              {order.customerOrderIndex === 1 && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  {t('packer.packingSlip.firstOrder')}
                </Badge>
              )}
            </div>
          </div>
          {order.isSingleLine && (
            <Badge variant="outline" className="text-xs">
              {t('packer.packingSlip.singleLine')}
            </Badge>
          )}
        </div>

        {/* Shipping address or pickup indicator */}
        {order.isPickup ? (
          <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <Store className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-lg font-bold text-amber-800 dark:text-amber-200">{t('packer.packingSlip.pickupOrder')}</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">{t('packer.packingSlip.pickupDesc')}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="text-sm leading-relaxed">
              {formattedAddress.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          </div>
        )}

        {/* Item summary badges */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-muted-foreground" />
            <Badge variant="secondary" className="text-sm font-medium">
              {t('nItemsTotal', { count: order.totalItems })}
            </Badge>
          </div>
          {order.podCount > 0 && (
            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              {order.podCount} POD
            </Badge>
          )}
          {order.papCount > 0 && (
            <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
              {order.papCount} P&P
            </Badge>
          )}
        </div>

        {/* A Hybrid parcel is deliberately SHORT of the item count above: Portless
            adds its own half from stock it already holds. Without this, a packer
            reports the shortfall as a picking error.

            A visible callout, not a badge with a tooltip: PRODUCTION_DESIGN.md §10
            bans tooltips carrying critical info ("hover doesn't exist" — these
            stations are tablets), and §4.2 sets the type floor above text-xs for
            anything an operator must read. Mirrors the pickup callout above, which
            is this file's established pattern for "change your behaviour".

            Hybrid only — Cross Dock is the normal case and needs no instruction. */}
        {order.fulfilmentType === 'hybrid' && (
          <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <PackageOpen className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="text-lg font-bold text-blue-800 dark:text-blue-200">
                {t('packer.packingSlip.hybridParcel')}
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {t('packer.packingSlip.hybridParcelDesc')}
              </p>
            </div>
          </div>
        )}

        {/* Container label for multi-line orders.
            BLU-197: a work bench (BNCH-…) renders a distinct indigo pill so a
            packer can tell at a glance whether to fetch from a bin or a bench. */}
        {order.binLabel && (() => {
          const isBench = order.isBench ?? order.binLabel.startsWith(BENCH_LABEL_PREFIX);
          return (
            <div className="flex items-center gap-3">
              {isBench ? (
                <>
                  <LayoutPanelTop className="w-5 h-5 text-indigo-600" />
                  <Badge
                    variant="outline"
                    className="text-lg font-bold border-indigo-500 text-indigo-700 dark:text-indigo-400 px-3 py-1"
                  >
                    {t('packer.benchPill', { benchLabel: order.binLabel })}
                  </Badge>
                </>
              ) : (
                <>
                  <Box className="w-5 h-5 text-amber-600" />
                  <Badge
                    variant="outline"
                    className="text-lg font-bold border-amber-500 text-amber-700 dark:text-amber-400 px-3 py-1"
                  >
                    {order.binLabel}
                  </Badge>
                </>
              )}
            </div>
          );
        })()}

        {/* Order date */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>{t('orderedDate', { date: formattedDate })}</span>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Format shipping address into display lines.
 * Handles missing/empty address fields gracefully.
 */
function formatAddress(address: ShippingAddress, t: (key: string) => string): string[] {
  const lines: string[] = [];

  // Only add address lines if they have content
  if (address.address1) {
    lines.push(address.address1);
  }
  if (address.address2) {
    lines.push(address.address2);
  }

  // Build city/province/zip line, handling empty values
  const cityProvince = [address.city, address.province].filter(Boolean).join(', ');
  const zipPart = address.zip ? ` ${address.zip}` : '';
  const locationLine = `${cityProvince}${zipPart}`.trim();
  if (locationLine) {
    lines.push(locationLine);
  }

  if (address.country && address.country !== 'US' && address.country !== 'United States') {
    lines.push(address.country);
  }

  // If no address info at all, show placeholder
  if (lines.length === 0) {
    lines.push(t('packer.packingSlip.addressNotAvailable'));
  }

  return lines;
}

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, 4th, etc.)
 */
function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? 'th');
}

/**
 * Format date for display.
 */
function formatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}
